import { computed, getCurrentInstance, onMounted, ref, unref } from 'vue';
import { normalizeInteger, normalizeLowercaseString, normalizeStringOrNull } from '@dashboardApp/helpers/normalize';
import useNotifications from '@dashboardApp/composables/useNotifications';
import { useTrackedProductIdeasStore } from '@dashboardApp/stores/TrackedProductIdeasStore';
import { useProductIdeaStore } from '@dashboardApp/stores/ProductIdeaStore';

export default function useProductIdeaSaveToggle(product) {
    const reportMutationInProgress = ref(false);
    const { addNotification } = useNotifications();
    const { createReport, deleteReport, deleteReportByProduct } = useProductIdeaStore();
    const trackedProductIdeasStore = useTrackedProductIdeasStore();

    const productIdea = computed(() => unref(product) ?? {});
    const productId = computed(() => normalizeId(productIdea.value.id ?? productIdea.value.product_id));
    const productName = computed(() => normalizeName(productIdea.value.name ?? productIdea.value.title ?? productIdea.value.term));
    const savedReport = computed(() => {
        return trackedProductIdeasStore.reportProducts.find((report) => {
            const reportProductId = normalizeId(report.product_id);

            if (productId.value !== null && reportProductId === productId.value) {
                return true;
            }

            return productName.value !== '' && normalizeName(report.term) === productName.value;
        }) ?? null;
    });
    const isReported = computed(() => savedReport.value !== null || normalizeReportId(productIdea.value.report_id) !== null);
    const savedProductId = computed(() => productId.value ?? normalizeId(savedReport.value?.product_id));
    const savedReportId = computed(() => normalizeReportId(productIdea.value.report_id ?? savedReport.value?.id));

    const ensureReportProductsLoaded = () => {
        if (!trackedProductIdeasStore.reportProducts.length) {
            void trackedProductIdeasStore.fetchReportProducts().catch(() => null);
        }
    };

    if (getCurrentInstance()) {
        onMounted(() => {
            ensureReportProductsLoaded();
        });
    } else {
        ensureReportProductsLoaded();
    }

    function saveOrRemove() {
        if (reportMutationInProgress.value) {
            return Promise.resolve(null);
        }

        reportMutationInProgress.value = true;

        if (!isReported.value) {
            return createReport(productIdea.value).then((data) => {
                if (data.id) {
                    addNotification({ message: 'The idea was saved successfully!' });
                    return trackedProductIdeasStore.fetchReportProducts({ force: true }).then(() => data);
                }

                return data;
            }).finally(() => {
                reportMutationInProgress.value = false;
            });
        }

        if (savedProductId.value === null && savedReportId.value === null) {
            reportMutationInProgress.value = false;
            return Promise.resolve(null);
        }

        const removePromise = savedProductId.value !== null
            ? deleteReportByProduct({
                ...productIdea.value,
                id: savedProductId.value,
            })
            : deleteReport({
                id: savedReportId.value,
            });

        return removePromise.then((data) => {
            if (data) {
                addNotification({ message: 'The idea was removed successfully!' });
                return trackedProductIdeasStore.fetchReportProducts({ force: true }).then(() => data);
            }

            return data;
        }).finally(() => {
            reportMutationInProgress.value = false;
        });
    }

    return {
        isReported,
        reportMutationInProgress,
        saveOrRemove,
    };
}

function normalizeId(value) {
    return normalizeInteger(value, null, { min: 0 });
}

function normalizeName(value) {
    return normalizeLowercaseString(value);
}

function normalizeReportId(value) {
    return normalizeStringOrNull(value);
}
